import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useRef } from 'react';
import { useBrand } from '|/brand';
import { FormStateContext } from '|/context';
import AppFrameworkContext from '|/helpers/app-framework';
import { useIsLoadingScreenShowing } from '|/helpers/loading-screens';

export const CART_STEP_TRANSITION_VITAL = 'cart_step_transition';
export const MAX_TRANSITION_MS = 30_000;

interface Transition {
  fromStep: string;
  startedAt: number;
  startTime: number;
  visibilityEpoch: number;
}

let pending: Transition | null = null;
let visibilityEpoch = 0;
let isListening = false;

const listenForHidden = () => {
  if (isListening) return;
  isListening = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      visibilityEpoch += 1;
      pending = null;
    }
  });
};

const startTransition = (fromStep: string) => {
  listenForHidden();
  if (document.visibilityState === 'hidden') {
    pending = null;
    return;
  }
  pending = {
    fromStep,
    startedAt: performance.now(),
    startTime: Date.now(),
    visibilityEpoch
  };
};

const claimTransition = (toStep: string) => {
  const claimed = pending;
  pending = null;
  return claimed?.fromStep === toStep ? null : claimed;
};

const reportTransition = (transition: Transition, toStep: string, brand: string) => {
  if (transition.visibilityEpoch !== visibilityEpoch) return;
  const duration = performance.now() - transition.startedAt;
  if (duration > MAX_TRANSITION_MS) return;
  // @datadog/browser-rum 7.15 — addDurationVital(name, { startTime, duration, context })
  datadogRum.addDurationVital(CART_STEP_TRANSITION_VITAL, {
    startTime: transition.startTime,
    duration,
    context: { fromStep: transition.fromStep, toStep, brand }
  });
};

/** Test-only: forget any transition in flight. */
export const resetStepTransitionTiming = () => {
  pending = null;
};

/**
 * Times a cart step transition from the moment one cart step unmounts until the
 * next cart step has mounted, nothing is loading and no loading screen has been
 * showing for two frames. Steps that fetch on their own (Carvana protection,
 * trade-in) show `Loading` meanwhile, which holds the transition open. A
 * transition is dropped if the tab is hidden, if a step outside the cart
 * mounts first, if it ends on the step it started from, if it takes longer
 * than MAX_TRANSITION_MS, or if the next step unmounts before it settles.
 */
const useStepTransitionTiming = (stepName: string, isCartStep: boolean) => {
  const { isLoading: isFormLoading } = useContext(FormStateContext);
  const { isLoading: isPageLoading } = useContext(AppFrameworkContext);
  const isLoadingScreenShowing = useIsLoadingScreenShowing();
  const { brand } = useBrand();
  const transition = useRef<Transition | null>(null);

  useEffect(() => {
    if (!stepName) return;
    const claimed = claimTransition(stepName);
    if (!isCartStep) return;
    transition.current = claimed;
    return () => {
      transition.current = null;
      startTransition(stepName);
    };
  }, [stepName, isCartStep]);

  const isSettled = !isFormLoading && !isPageLoading && !isLoadingScreenShowing;

  useEffect(() => {
    if (!isSettled || transition.current === null) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        const settled = transition.current;
        transition.current = null;
        if (settled !== null) reportTransition(settled, stepName, brand);
      });
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [isSettled, stepName, brand]);
};

export default useStepTransitionTiming;
